// 封装主 API 请求，统一处理业务错误和登录令牌失效。
import axios, { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { useUserStoreHook } from "@/store/modules/user";
import { ResultEnum } from "@/enums/ResultEnum";
import { TOKEN_KEY } from "@/enums/CacheEnum";
import type {
  ApiResponseEnvelope,
  RequestConfigWithErrorHandler,
} from "@/types/request";
import qs from "qs";
import {
  retryAfterTokenRefresh,
  shouldSkipTokenRefresh,
} from "./request/tokenRefresh";

// 并发接口可能同时返回失效，刷新登录状态前只提示一次。
let isLoginExpired = false;

/**
 * 判断业务响应码是否表示登录令牌失效。
 * @param code 接口返回的业务响应码
 * @returns 是否需要重新登录
 */
function isTokenInvalid(code: unknown): boolean {
  return code === 401 || code === "401";
}

/** 清理过期登录状态并提示用户重新登录。 */
function handleLoginExpired(): void {
  if (isLoginExpired) return;
  isLoginExpired = true;
  ElNotification({
    title: "提示",
    message: "登录已过期，请重新登录",
    type: "info",
  });
  useUserStoreHook()
    .resetToken()
    .then(() => {
      location.reload();
    })
    .catch(() => {
      // 清理失败时允许后续请求再次触发登录引导。
      isLoginExpired = false;
    });
}

// 创建 axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  timeout: 50000,
  headers: { "Content-Type": "application/json;charset=utf-8" },
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: "repeat" });
  },
});

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem(TOKEN_KEY);
    if (!shouldSkipTokenRefresh(config) && accessToken) {
      config.headers.Authorization = /^bearer\s/i.test(accessToken)
        ? accessToken
        : `bearer ${accessToken}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 检查配置的响应类型是否为二进制类型（'blob' 或 'arraybuffer'）, 如果是，直接返回响应对象
    if (
      response.config.responseType === "blob" ||
      response.config.responseType === "arraybuffer"
    ) {
      return response;
    }

    const { code, data, msg } = response.data as ApiResponseEnvelope;
    // HTTP 200 也可能携带令牌失效业务码，必须先于通用错误处理。
    if (isTokenInvalid(code) && !shouldSkipTokenRefresh(response.config)) {
      return retryAfterTokenRefresh(
        service,
        response.config,
        handleLoginExpired
      );
    }
    const isSuccess = code === ResultEnum.SUCCESS || code === 0 || code === "0";
    if (isSuccess) {
      // 运行时沿用业务 data 解包；Axios 拦截器签名仍按完整响应声明。
      return data as never;
    }

    const config = response.config as RequestConfigWithErrorHandler;
    const error = new Error(msg || "Error");
    if (config?.onError && typeof config.onError === "function") {
      const shouldShowDefault = config.onError(error, response.data) !== false;
      if (!shouldShowDefault) {
        return Promise.reject(error);
      }
    }

    ElMessage.error(msg || "系统出错");
    return Promise.reject(error);
  },
  (error: any) => {
    // HTTP 401 不依赖响应正文，页面自定义错误回调也不应屏蔽登录失效提示。
    if (
      error?.response?.status === 401 ||
      isTokenInvalid(error?.response?.data?.code)
    ) {
      if (error.config && !shouldSkipTokenRefresh(error.config)) {
        return retryAfterTokenRefresh(
          service,
          error.config,
          handleLoginExpired
        );
      }
    }
    const config = error.config as RequestConfigWithErrorHandler | undefined;

    if (config?.onError && typeof config.onError === "function") {
      const shouldShowDefault =
        config.onError(error, error?.response?.data) !== false;
      if (!shouldShowDefault) {
        return Promise.reject(error);
      }
    }

    // 异常处理
    if (error?.response?.data) {
      const { msg } = error.response.data;
      ElMessage.error(msg || "系统出错");
    }
    return Promise.reject(error.message);
  }
);

// 导出 axios 实例
export default service;
